import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { api, type Book, type Bookmark } from "../api";
import { BookCover } from "../components/BookCover";
import { Button, Card, EmptyState, Input, SectionHeading, Spinner } from "../components/ui";

export default function QuotesPage() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const list = useQuery({
    queryKey: ["bookmarks", "mine"],
    queryFn: () => api.get<{ bookmarks: Bookmark[] }>("/bookmarks/mine"),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/bookmarks/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bookmarks", "mine"] }),
  });

  const bookmarks = list.data?.bookmarks ?? [];

  return (
    <div className="flex flex-col gap-10">
      <NotebookNav />

      <div className="flex items-end justify-between gap-4">
        <SectionHeading
          eyebrow="Your commonplace book"
          title="Quotes"
          description="Lines you wanted to remember. Yours alone — the club doesn't see these."
        />
        <Button onClick={() => setShowForm((v) => !v)} variant={showForm ? "ghost" : "primary"}>
          {showForm ? "Cancel" : "Save a quote"}
        </Button>
      </div>

      {showForm ? (
        <QuoteForm
          onSaved={() => {
            setShowForm(false);
            qc.invalidateQueries({ queryKey: ["bookmarks", "mine"] });
          }}
        />
      ) : null}

      {list.isLoading ? (
        <Spinner />
      ) : bookmarks.length === 0 ? (
        <Card accent>
          <EmptyState
            title="No quotes yet"
            description="Save a line that struck you. It's a small, lovely thing to look back on."
            action={<Button onClick={() => setShowForm(true)}>Save your first quote</Button>}
          />
        </Card>
      ) : (
        <div className="flex flex-col gap-6">
          {bookmarks.map((b) => (
            <Card key={b.id} accent>
              <div className="flex flex-col md:flex-row gap-6">
                <Link to={`/book/${b.book.id}`} className="shrink-0">
                  <BookCover url={b.book.coverUrl} title={b.book.title} author={b.book.author} size="md" />
                </Link>
                <div className="flex-1 min-w-0">
                  <blockquote className="font-serif italic text-xl text-plum leading-snug">
                    "{b.quote}"
                  </blockquote>
                  <div className="mt-3 text-xs text-plum-mute flex items-center gap-3 flex-wrap">
                    <Link to={`/book/${b.book.id}`} className="text-plum hover:text-rose">
                      {b.book.title}
                    </Link>
                    <span>·</span>
                    <span className="italic">{b.book.author}</span>
                    {b.chapter ? <><span>·</span><span>ch. {b.chapter}</span></> : null}
                    {b.page ? <><span>·</span><span>p. {b.page}</span></> : null}
                    <span>·</span>
                    <span>{format(parseISO(b.createdAt.replace(" ", "T")), "MMM d, yyyy")}</span>
                  </div>
                  {b.note ? (
                    <p className="mt-3 text-plum-soft text-sm leading-relaxed border-l-2 border-blush-soft pl-3">
                      {b.note}
                    </p>
                  ) : null}
                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={() => remove.mutate(b.id)}
                      className="text-[0.65rem] uppercase tracking-[0.16em] text-plum-mute hover:text-rose"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function QuoteForm({ onSaved }: { onSaved: () => void }) {
  const [bookId, setBookId] = useState<number | "">("");
  const [quote, setQuote] = useState("");
  const [chapter, setChapter] = useState("");
  const [page, setPage] = useState("");
  const [note, setNote] = useState("");

  const books = useQuery({
    queryKey: ["books"],
    queryFn: () => api.get<{ books: Book[] }>("/books"),
  });

  const save = useMutation({
    mutationFn: (payload: { bookId: number; quote: string; chapter: number | null; page: number | null; note: string | null }) =>
      api.post("/bookmarks", payload),
    onSuccess: onSaved,
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (bookId === "" || !quote.trim()) return;
    save.mutate({
      bookId: Number(bookId),
      quote: quote.trim(),
      chapter: chapter ? Number(chapter) : null,
      page: page ? Number(page) : null,
      note: note.trim() || null,
    });
  }

  return (
    <Card accent>
      <form onSubmit={submit} className="flex flex-col gap-5">
        <div>
          <label className="text-xs font-medium uppercase tracking-[0.12em] text-plum-soft block mb-1.5">
            From which book
          </label>
          <select
            value={String(bookId)}
            onChange={(e) => setBookId(e.target.value === "" ? "" : Number(e.target.value))}
            required
            className="w-full px-4 py-3 rounded-xl bg-cream-50 border border-cream-300 text-plum focus:outline-none focus:border-rose"
          >
            <option value="">Choose a book…</option>
            {(books.data?.books ?? []).map((b) => (
              <option key={b.id} value={b.id}>
                {b.title}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs font-medium uppercase tracking-[0.12em] text-plum-soft block mb-1.5">
            The quote
          </label>
          <textarea
            value={quote}
            onChange={(e) => setQuote(e.target.value)}
            required
            rows={4}
            placeholder="The line you want to remember…"
            className="w-full px-4 py-3 rounded-xl bg-cream-50 border border-cream-300 text-plum placeholder:text-plum-mute placeholder:italic focus:outline-none focus:border-rose resize-none font-serif italic"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Chapter (optional)"
            type="number"
            value={chapter}
            onChange={(e) => setChapter(e.target.value)}
            placeholder="3"
          />
          <Input
            label="Page (optional)"
            type="number"
            value={page}
            onChange={(e) => setPage(e.target.value)}
            placeholder="142"
          />
        </div>

        <div>
          <label className="text-xs font-medium uppercase tracking-[0.12em] text-plum-soft block mb-1.5">
            Your note (optional)
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="Why this struck you…"
            className="w-full px-4 py-3 rounded-xl bg-cream-50 border border-cream-300 text-plum placeholder:text-plum-mute placeholder:italic focus:outline-none focus:border-rose resize-none"
          />
        </div>

        <div className="flex justify-end">
          <Button type="submit" loading={save.isPending}>Save quote</Button>
        </div>
      </form>
    </Card>
  );
}

export function NotebookNav() {
  return (
    <div className="flex items-center gap-2">
      <NavTab to="/notebook/quotes" label="Quotes" />
      <NavTab to="/notebook/recipes" label="Recipes" />
    </div>
  );
}

function NavTab({ to, label }: { to: string; label: string }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        [
          "px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.16em] transition-colors",
          isActive
            ? "bg-rose text-white"
            : "bg-cream-100 text-plum-soft hover:bg-blush-soft hover:text-rose-dark",
        ].join(" ")
      }
    >
      {label}
    </NavLink>
  );
}
